import React, { useEffect, useState } from 'react';

import toast from 'react-hot-toast';
import { t } from 'i18next';
import { useFormik } from 'formik';
import axios from 'axios';

import Yup from '../../../../utils/yupExtensions';
import styles from './AboutMeSection.module.css';
import Button from '../../Buttons/Button/Button';
import FormField from '../../FormUI/FormField/FormField';
import FormRichTextField from '../../FormUI/FormRichTextField/FormRichTextField';
import { Close } from '@mui/icons-material';
import { updateUser } from '../../../../services/userService';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchUserByToken, fetchUserByTokenWithoutLoading, updateUserProperty } from '../../../../app/features/userSlice';
import { useDispatch, useSelector } from 'react-redux';
import { EditIcon, VerifyBadge } from '../../../../assets/images/icons';
import userAvatarPlaceholder from '../../../../assets/images/other/user_avatar_placeholder.png';
import FormOptionField from '../../FormUI/FormOptionField/FormOptionField';
import { useGetRoleBasedSkillsHook } from '../../../../utils/utilityHooks';

import { RiLink } from "react-icons/ri";

function AboutMeSection({ jobseeker, isemployer }) {
    const [isEditing, setIsEditing] = useState(false);
    const [editedJobseeker, setEditedJobseeker] = useState({ ...jobseeker });
    const [formLoading, setFormLoading] = useState(false);
    const [profileImage, setProfileImage] = useState(jobseeker?.profile_image ? jobseeker?.profile_image : null); // For handling profile image change
    const [imagePreview, setImagePreview] = useState(''); // For displaying image preview
    
    // Job title suggestions
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [titleSuggestions, setTitleSuggestions] = useState([]);
    const [titleInput, setTitleInput] = useState('');
    const jobTitleOptions = useGetRoleBasedSkillsHook();

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { slug } = useParams();

    const userToken = useSelector(state => state.user.token);
    const user = useSelector((state) => state.user.user);

    const formik = useFormik({
        initialValues: {
            first_name: jobseeker.first_name || '',
            profile_visibility: user.profile_visibility ? true : false,
            hide_Profile_Picture: user.hide_Profile_Picture ? true : false,
            last_name: jobseeker.last_name || '',
            working_title: isemployer ? jobseeker.role : jobseeker.working_title || '',
            description: jobseeker.description || '',
        },
        validationSchema: Yup.object({
            first_name: Yup.string().required(t('general.UI.required')),
            last_name: Yup.string().required(t('general.UI.required')),
        }),
        onSubmit: async (values) => {
            if (formLoading) return;
            setFormLoading(true);
            const formData = new FormData();
            formData.append('token', userToken);
            formData.append('first_name', values.first_name);
            formData.append('last_name', values.last_name);
            formData.append('description', values.description);
            if (!isemployer) {
                formData.append('profile_visibility', values.profile_visibility);
                formData.append('hide_Profile_Picture', values.hide_Profile_Picture);
            }
            
            // If title doesn't exist in the available options, create it
            const titleExists = jobTitleOptions.some(option => 
                option.label?.toLowerCase() === values.working_title?.toLowerCase()
            );
            
            // Include flag for new title creation if needed
            if (!titleExists && values.working_title?.trim()) {
                formData.append('create_new_title', 'true');
            }
            
            if (isemployer) formData.append('role', values.working_title);
            else formData.append('working_title', values.working_title);
            
            if (profileImage) formData.append('profile_image', profileImage); // Append the new profile image
            setIsEditing(true)
            setFormLoading(true);
            toast.promise(updateUser(formData), {
                loading: t('edit_profile.updating_profile'),
                success: <b>{t('edit_profile.profile_updated')}</b>,
                error: (err) => {
                    return <b>{err.response.data.error}</b>;
                },
            })
                .then(async () => {
                    const newSlug = `${values.first_name.trim().replace(/\s+/g, '-').toLowerCase()}-${values.last_name.trim().replace(/\s+/g, '-').toLowerCase()}`;
                    jobseeker.description = values.description
                    jobseeker.slug = newSlug
                    jobseeker.first_name = values.first_name
                    jobseeker.last_name = values.last_name
                    dispatch(updateUserProperty({ key: 'slug', value: newSlug }));
                    dispatch(updateUserProperty({ key: 'first_name', value: values.first_name }));
                    dispatch(updateUserProperty({ key: 'last_name', value: values.last_name }));
                    jobseeker.working_title = values.working_title
                    jobseeker.profile_image = profileImage
                    if (user) {
                        navigate(`/profile`);
                    }
                    setIsEditing(false)
                    setFormLoading(false);
                })
                .catch((error) => {
                    setFormLoading(false);
                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }
                });
        },
    });

    useEffect(() => {
        setEditedJobseeker(jobseeker);
    }, [jobseeker]);

    // Handle title input change and show suggestions
    const handleTitleInputChange = (e) => {
        const value = e.target.value;
        formik.setFieldValue('working_title', value);
        setTitleInput(value);
        
        if (value.trim()) {
            // Filter job title options that match the input
            const filteredOptions = jobTitleOptions.filter(option => 
                option.label?.toLowerCase().includes(value.toLowerCase())
            );
            setTitleSuggestions(filteredOptions.slice(0, 5)); // Limit to top 5 matches
            setShowSuggestions(true);
        } else {
            setShowSuggestions(false);
        }
    };

    // Handle selecting a title from suggestions
    const handleSelectTitle = (title) => {
        formik.setFieldValue('working_title', title.label);
        setTitleInput(title.label);
        setShowSuggestions(false);
    };

    const handleEditToggle = () => {
        setIsEditing(!isEditing);
        // Reset the title input when editing
        if (!isEditing) {
            setTitleInput(formik.values.working_title);
        }
    };

    const handleImageChange = (event) => {
        const file = event.target.files[0];
        if (file) {
            setProfileImage(file);
            // Create a preview URL for the selected image
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const stripHTML = (input) => {
        const allowedTags = ['b', 'i', 'strong', 'em', 'p', 'br', 'ul', 'ol', 'li'];
        const doc = new DOMParser().parseFromString(input, 'text/html');
        const elements = doc.body.querySelectorAll('*');

        elements.forEach(element => {
            if (!allowedTags.includes(element.nodeName.toLowerCase())) {
                element.replaceWith(...element.childNodes);
            }
        });

        return doc.body.innerHTML;
    };

    return (
        <div className={styles.container}>
            {isEditing &&
                <Close
                    className={styles.mobileIcon}
                    style={{
                        cursor: "pointer"
                    }}
                    onClick={() => { handleEditToggle() }}
                />
            }
            <div className={styles.firstColumn}>
                <div className={styles.profileImageContainer}>
                    {isEditing ?
                        <>
                            <img
                                className={styles.profileImage}
                                src={imagePreview || (editedJobseeker?.profile_image ? `${process.env.REACT_APP_UPLOADS_PATH}${editedJobseeker?.profile_image}` : userAvatarPlaceholder)}
                                onError={(e) => {
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.src = userAvatarPlaceholder; // Set fallback image
                                }}
                                alt={`${editedJobseeker?.first_name} ${editedJobseeker?.last_name}`}
                            />
                            <label >
                                <img
                                    className={`${styles.onlineStatus} ${styles.uploadIcon}`}
                                    src={EditIcon}
                                    style={{
                                        backgroundColor: "white",
                                        height: 20,
                                        width: 20,
                                        cursor: 'pointer',
                                    }}
                                    alt={"DownloadIcon"}
                                />
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleImageChange}
                                    style={{ display: "none" }}
                                    className={styles.fileInput}
                                />
                            </label>
                        </>
                        :
                        <>
                            <img
                                className={styles.profileImage}
                                src={
                                    editedJobseeker?.profile_image instanceof File
                                        ? URL.createObjectURL(editedJobseeker?.profile_image)
                                        : editedJobseeker?.profile_image
                                            ? `${process.env.REACT_APP_UPLOADS_PATH}${editedJobseeker?.profile_image}`
                                            : userAvatarPlaceholder
                                }
                                onError={(e) => {
                                    e.target.onerror = null; // Prevent infinite loop
                                    e.target.src = userAvatarPlaceholder; // Set fallback image
                                }}
                                alt={`${editedJobseeker?.first_name} ${editedJobseeker?.last_name}`}
                            />
                            <div className={styles.onlineStatus}></div>
                        </>
                    }
                </div>
            </div>

            <div className={styles.secondColumn}>
                <div className={`${styles.title} ${styles.centerImg}`}>
                    {isEditing ? (
                        <div className={styles.editNameContainer}>
                            <FormField
                                name="first_name"
                                label={t('forms.welcome_job_seeker.step_1.first_name')}
                                hasBorder
                                value={formik.values.first_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.first_name && formik.errors.first_name}
                            />
                            <FormField
                                name="last_name"
                                label={t('forms.welcome_job_seeker.step_1.last_name')}
                                hasBorder
                                value={formik.values.last_name}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                error={formik.touched.last_name && formik.errors.last_name}
                            />
                        </div>
                    ) : (
                        <>
                            {editedJobseeker?.first_name} {editedJobseeker?.last_name}
                        </>
                    )}
                    {editedJobseeker?.verification_status === 'approved' && (
                        <img src={VerifyBadge} alt="Verified" className={styles.image} />
                    )}
                </div>
                {isemployer ?
                    isEditing ? ( 
                        <div className={styles.titleInputContainer}>
                            <FormField
                                name="working_title"
                                label={t('employerPersonalInformation.role')}
                                hasBorder
                                value={titleInput || formik.values.working_title}
                                onChange={handleTitleInputChange}
                                onBlur={() => {
                                    // Keep suggestions open for a moment to allow clicking
                                    setTimeout(() => setShowSuggestions(false), 200);
                                }}
                                error={formik.touched.working_title && formik.errors.working_title}
                            />
                            {showSuggestions && titleSuggestions.length > 0 && (
                                <div className={styles.suggestionsList}>
                                    {titleSuggestions.map(title => (
                                        <div 
                                            key={title.id} 
                                            className={styles.suggestionItem}
                                            onClick={() => handleSelectTitle(title)}
                                        >
                                            {title.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        editedJobseeker?.role && (
                            <div className={styles.skillsContainer}>
                                <div className={styles.skillTag}>{editedJobseeker?.role}</div>
                            </div>
                        )
                    ) :
                    isEditing ? (
                        <div className={styles.titleInputContainer}>
                            <FormField
                                name="working_title"
                                label={t('edit_profile.job_seeker.working_title')}
                                hasBorder
                                value={titleInput || formik.values.working_title}
                                onChange={handleTitleInputChange}
                                onBlur={() => {
                                    // Keep suggestions open for a moment to allow clicking
                                    setTimeout(() => setShowSuggestions(false), 200);
                                }}
                                error={formik.touched.working_title && formik.errors.working_title}
                            />
                            {showSuggestions && titleSuggestions.length > 0 && (
                                <div className={styles.suggestionsList}>
                                    {titleSuggestions.map(title => (
                                        <div 
                                            key={title.id} 
                                            className={styles.suggestionItem}
                                            onClick={() => handleSelectTitle(title)}
                                        >
                                            {title.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    ) : (
                        editedJobseeker?.working_title && (
                            <div className={styles.skillsContainer}>
                                <div className={styles.skillTag}>{editedJobseeker?.working_title}</div>
                            </div>
                        )
                    )

                }
                {!isEditing && (
                    <div className={styles.aboutTitle}>{t('aboutMe.aboutTitle')}</div>
                )}
                {isEditing ? (
                    <>
                        <FormRichTextField
                            name="description"
                            label={t('edit_profile.job_seeker.description')}
                            hasBorder
                            value={formik.values.description}
                            onFormikChange={formik.handleChange}
                            error={formik.touched.description && formik.errors.description}
                        />
                        {!isemployer &&
                            <>     <div className={styles.profile_visibility}>
                                <FormOptionField
                                    name="profile_visibility"
                                    dark
                                    type="checkbox"
                                    options={[
                                        { value: true, label: t('edit_profile.job_seeker.show_profile_in_results') },
                                    ]}
                                    onChange={formik.handleChange}
                                    value={formik.values.profile_visibility}
                                />
                            </div>
                                {
                                    user?.verification_status === "approved" &&
                                    <div className={styles.profile_visibility}>
                                        <FormOptionField
                                            name="hide_Profile_Picture"
                                            dark
                                            type="checkbox"
                                            options={[
                                                { value: true, label: t('edit_profile.job_seeker.Hide_Profile_Picture') },
                                            ]}
                                            onChange={formik.handleChange}
                                            value={formik.values.hide_Profile_Picture}
                                        />
                                    </div>
                                }
                            </>}
                    </>
                ) : (
                    <div
                        className={styles.description}
                        dangerouslySetInnerHTML={{
                            __html: jobseeker?.description ? stripHTML(jobseeker?.description) : '',
                        }}
                    ></div>
                )}
                {isEditing &&
                    <div style={{ textAlign: 'center' }}>
                        <Button onClick={() => { formik.submitForm() }}>
                            {formLoading ? t('general.UI.loading') : t('general.UI.save')}
                        </Button>
                    </div>
                }
            </div>

            <div className={styles.thirdColumn}>
                {isEditing &&
                    <Close
                        className={styles.crossIcon}
                        style={{
                            cursor: "pointer"
                        }}
                        onClick={() => { handleEditToggle() }}
                    />
                }
                {!isEditing &&
                    <div className={styles.previewLink}>
                        <RiLink onClick={() => {
                            const link = `${process.env.REACT_APP_CLIENT_URL}/jobseekers/${slug}`;
                            navigator.clipboard.writeText(link)
                                .then(() => {
                                    toast.success(t('coppied'));
                                })
                                .catch((err) => {
                                });
                        }}
                            className={styles.editIcon} />
                        <img
                            src={EditIcon}
                            alt={t('aboutMe.edit')}
                            className={styles.editIcon}
                            onClick={handleEditToggle}
                        />
                    </div>
                }
            </div>
        </div >
    );
}

export default AboutMeSection;






